/**
 * American Vek: airflow calculator. Minimum airflow = room volume × air changes per hour.
 * Diameter reference (same as the American Vek blog guide): 100 mm up to ~106 m³/h, 125 mm up to ~195,
 * 150 mm up to ~368, 200 mm+ up to ~1,200, 250 mm+ above that.
 */
const DIAMETERS = [
  { max: 106, label: '100 mm', kit: true },
  { max: 195, label: '125 mm', kit: true },
  { max: 368, label: '150 mm', kit: true },
  { max: 1200, label: '200 mm ou mais', kit: false },
  { max: Infinity, label: '250 mm ou mais', kit: false },
];

const number = (value) => Number(String(value).replace(',', '.'));
const fmt = (value, digits = 0) =>
  value.toLocaleString('pt-BR', { minimumFractionDigits: digits, maximumFractionDigits: digits });

class AvAirflowCalculator extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.form?.addEventListener('submit', this.onSubmit);
  }

  /** @param {string} name */
  out(name) {
    return /** @type {HTMLElement} */ (this.querySelector(`[data-out="${name}"]`));
  }

  /** @param {SubmitEvent} event */
  onSubmit = (event) => {
    event.preventDefault();
    const data = new FormData(this.form);
    const c = number(data.get('c'));
    const l = number(data.get('l'));
    const a = number(data.get('a'));
    const t = number(data.get('t'));
    const error = /** @type {HTMLElement} */ (this.querySelector('.av-airflow__error'));
    const result = /** @type {HTMLElement} */ (this.querySelector('.av-airflow__result'));
    if (!(c > 0 && l > 0 && a > 0)) {
      error.hidden = false;
      return;
    }
    error.hidden = true;

    const volume = c * l * a;
    const flow = Math.ceil(volume * t);
    const diameter = DIAMETERS.find((d) => flow <= d.max) || DIAMETERS[DIAMETERS.length - 1];

    this.out('vol').textContent = `${fmt(volume, 1)} m³`;
    this.out('flow').textContent = `${fmt(flow)} m³/h`;
    this.out('diam').textContent = diameter.label;

    const link = /** @type {HTMLAnchorElement} */ (this.out('link'));
    if (diameter.kit) {
      this.out('advice').textContent = `Para ${fmt(volume, 1)} m³ com ${t} trocas por hora, um exaustor de ${diameter.label} com pelo menos ${fmt(flow)} m³/h atende. Os kits já vêm com duto, abraçadeiras e grelha.`;
      link.textContent = `Ver kits de ${diameter.label}`;
      link.href = this.dataset.kitsUrl || '/collections/kit-exaustor-residencial';
    } else {
      this.out('advice').textContent = `${fmt(flow)} m³/h está acima dos kits residenciais. Para esse ambiente o indicado é um exaustor de maior porte (axial ou centrífugo); nossa equipe ajuda a dimensionar.`;
      link.textContent = 'Ver exaustores de maior porte';
      link.href = this.dataset.largeUrl || '/collections/exaustores';
    }

    const wa = /** @type {HTMLAnchorElement|null} */ (this.out('wa'));
    if (wa && this.dataset.whatsapp) {
      const message = `Olá! Calculei ${fmt(flow)} m³/h para um ambiente de ${fmt(volume, 1)} m³ (${t} trocas/h, diâmetro ${diameter.label}). Podem me ajudar a escolher?`;
      wa.href = `https://wa.me/${this.dataset.whatsapp}?text=${encodeURIComponent(message)}`;
    }

    result.hidden = false;
  };
}

if (!customElements.get('av-airflow-calculator')) customElements.define('av-airflow-calculator', AvAirflowCalculator);
